import { useEffect, useState } from 'react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import {
  getFollowUpHistory,
  type FollowUpHistoryItem,
} from '@/lib/api/v2/follow-ups'

interface FollowUpHistoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  clientName: string
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso)
    return new Intl.DateTimeFormat('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d)
  } catch {
    return iso
  }
}

export function FollowUpHistoryDialog({
  open,
  onOpenChange,
  projectId,
  clientName,
}: FollowUpHistoryDialogProps) {
  const [items, setItems] = useState<FollowUpHistoryItem[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [reloadKey, setReloadKey] = useState(0)

  // Lazy fetch on open (and on retry via reloadKey).
  useEffect(() => {
    if (!open) return
    let cancelled = false
    /* eslint-disable react-hooks/set-state-in-effect */
    setLoading(true)
    setError(null)
    /* eslint-enable react-hooks/set-state-in-effect */
    getFollowUpHistory(projectId)
      .then((r) => {
        if (cancelled) return
        setItems(r.items)
      })
      .catch((e) => {
        if (cancelled) return
        setError(e)
        setItems(null)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, projectId, reloadKey])

  // Reset state on close so the next open re-fetches fresh.
  useEffect(() => {
    if (!open) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setItems(null)
      setError(null)
      setLoading(false)
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Historique des relances — {clientName}</DialogTitle>
        </DialogHeader>

        <div className="py-2 max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="space-y-3" data-testid="follow-up-history-loading">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          )}

          {!loading && error != null && (
            <div className="rounded-md border bg-background p-4 text-sm">
              <div className="font-medium">Impossible de charger l'historique.</div>
              <div className="mt-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setReloadKey((k) => k + 1)}
                  data-testid="follow-up-history-retry-btn"
                >
                  Réessayer
                </Button>
              </div>
            </div>
          )}

          {!loading && !error && items && items.length === 0 && (
            <div className="rounded-md border bg-background p-4 text-sm text-muted-foreground">
              Aucune relance loggée sur ce projet.
            </div>
          )}

          {!loading && !error && items && items.length > 0 && (
            <ul className="space-y-3" data-testid="follow-up-history-list">
              {items.map((it) => (
                <li key={it.id} className="border-b pb-3 last:border-b-0 last:pb-0">
                  <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
                    <span>{formatDateTime(it.occurredAt)}</span>
                    <span className="font-medium text-foreground">{it.actorName}</span>
                  </div>
                  <div className="mt-1 text-sm italic">«&nbsp;{it.note}&nbsp;»</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
